// ProcOps design-comp kit — reusable primitives mirroring the approved
// reference design. Inline styles resolve to the App.css palette tokens (which
// were re-pointed to the reference values), so these stay theme-consistent.
//
// Exports: tier, Badge, StatusBadge, Card, PageHeader, Kpi, KpiRow, SubTabs,
// SectionHeading, DataTable.

import { useId, useMemo, useRef, useState } from "react";
import { Info } from "lucide-react";

/** Little info icon that reveals a natural-language explanation on hover/focus.
 *  Used on KPI cards to explain how the metric is computed. Flips to
 *  right-anchored automatically when the icon is near the viewport's right
 *  edge so the bubble never clips off-screen. */
export function InfoTip({ text, testid }) {
  const [open, setOpen] = useState(false);
  const [align, setAlign] = useState("left");
  const wrapRef = useRef(null);
  const BUBBLE_W = 240;
  const show = () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect && rect.left + BUBBLE_W + 16 > window.innerWidth) setAlign("right");
    else setAlign("left");
    setOpen(true);
  };
  const hide = () => setOpen(false);
  if (!text) return null;
  return (
    <span
      ref={wrapRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={(e) => { e.stopPropagation(); if (open) hide(); else show(); }}
      data-testid={testid}
      tabIndex={0}
      style={{ position: "relative", display: "inline-flex", alignItems: "center",
        marginLeft: 6, color: "#786a97", cursor: "help", verticalAlign: "middle", outline: "none" }}
    >
      <Info size={13} strokeWidth={2.2} />
      {open && (
        <span
          role="tooltip"
          style={{
            position: "absolute", top: "calc(100% + 6px)", zIndex: 40,
            ...(align === "right" ? { right: 0, left: "auto" } : { left: 0, right: "auto" }),
            width: BUBBLE_W, padding: "10px 12px",
            background: "#17102b", color: "#f5f2fb",
            border: "1px solid #2a1c47", borderRadius: 8,
            fontSize: 12, lineHeight: 1.45, fontWeight: 500,
            textTransform: "none", letterSpacing: "normal",
            boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
            whiteSpace: "normal", pointerEvents: "none",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

/* ── 3-tier status colouring: green ≥ good, amber ≥ ok, red below ── */
export function tier(pct, good, ok) {
  const n = Number(pct);
  if (!Number.isFinite(n)) return { bg: "rgba(120,106,151,0.15)", color: "#786a97" };
  if (n >= good) return { bg: "rgba(52,211,153,0.15)", color: "#34d399" };
  if (n >= ok) return { bg: "rgba(251,191,36,0.15)", color: "#fbbf24" };
  return { bg: "rgba(251,113,133,0.15)", color: "#fb7185" };
}

const TONES = {
  green: { bg: "rgba(52,211,153,0.15)", color: "#34d399" },
  amber: { bg: "rgba(251,191,36,0.15)", color: "#fbbf24" },
  red: { bg: "rgba(251,113,133,0.15)", color: "#fb7185" },
  pink: { bg: "rgba(236,72,153,0.15)", color: "#ec4899" },
  cyan: { bg: "rgba(103,224,240,0.15)", color: "#67e0f0" },
  muted: { bg: "rgba(255,255,255,0.06)", color: "#9d8fc0" },
};

/** Pill badge. Pass `tone` (green/amber/red/pink/cyan/muted) or explicit bg/color. */
export function Badge({ children, tone = "muted", bg, color, style = {} }) {
  const t = TONES[tone] || TONES.muted;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 999,
      fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap",
      background: bg || t.bg, color: color || t.color, ...style,
    }}>
      {children}
    </span>
  );
}

/** Numeric badge coloured by the 3-tier thresholds. */
export function StatusBadge({ value, good, ok, suffix = "%" }) {
  const t = tier(value, good, ok);
  return <Badge bg={t.bg} color={t.color}>{value}{suffix}</Badge>;
}

/** Card surface (#17102b). `accent` adds a coloured border (e.g. pink hero). */
export function Card({ children, accent, style = {}, ...rest }) {
  return (
    <div
      style={{
        background: "var(--color-bg-hemocyanin)",
        minWidth: 0, maxWidth: "100%",
        border: `1px solid ${accent || "var(--hairline)"}`,
        borderRadius: 16, padding: "22px 24px", ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Page title (Sora) + subtitle. */
export function PageHeader({ title, subtitle, right }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 28 }}>
      <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
        <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 700, color: "var(--color-text-1)" }}>{title}</h1>
        {subtitle && <div style={{ fontSize: 15, color: "var(--color-text-3)", marginTop: 6 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

/** KPI tile: uppercase label + Sora value. Optional `hint` renders an info
 *  icon next to the label with a natural-language tooltip explaining the metric. */
export function Kpi({ label, value, hint, labelColor = "var(--color-text-4)", valueColor = "var(--color-text-1)", size = 30 }) {
  // Flex column + bottom-anchored value: when labels wrap to different line
  // counts within a row, the values still align on a common baseline.
  return (
    <Card style={{ padding: "18px 20px", borderRadius: 14, display: "flex", flexDirection: "column", overflow: "visible" }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: labelColor, display: "flex", alignItems: "center" }}>
        <span>{label}</span>
        {hint && <InfoTip text={hint} testid={`kpi-hint-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} />}
      </div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: size, fontWeight: 700, marginTop: "auto", paddingTop: 8, color: valueColor }}>{value}</div>
    </Card>
  );
}

/** Responsive KPI row. `cols` sets the grid column count. */
export function KpiRow({ items, cols }) {
  const n = cols || items.length;
  // Responsive without a stylesheet: keep the tiles in one row on a wide screen,
  // then wrap instead of squashing on a narrow laptop window, tablet or phone.
  // The min(100%, …) floor stops a single tile overflowing a very narrow container.
  // A ~180px floor keeps money values readable before it wraps.
  const minW = Math.max(130, Math.floor(1040 / Math.max(n, 1)));
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${minW}px), 1fr))`, gap: 14, marginBottom: 20 }}>
      {items.map((k, i) => <Kpi key={i} {...k} />)}
    </div>
  );
}

/** Section heading (Sora) with optional right-hand slot + subtitle. */
export function SectionHeading({ title, subtitle, right, style = {} }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 16, flexWrap: "wrap", ...style }}>
      <div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700, color: "var(--color-text-1)" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: "var(--color-text-3)", marginTop: 3 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

/** Pill sub-tab switcher. tabs = [{ key, label }]. */
export function SubTabs({ tabs, active, onChange, style = {} }) {
  const id = useId().replace(/:/g, "");
  return (
    <div style={{ display: "flex", gap: 8, background: "var(--color-bg-hemocyanin)", border: "1px solid var(--hairline)", borderRadius: 12, padding: 6, width: "fit-content", maxWidth: "100%", overflowX: "auto", ...style }}>
      {tabs.map((t) => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            data-testid={`subtab-${id}-${t.key}`}
            aria-pressed={on}
            onClick={() => onChange(t.key)}
            style={{
              border: 0, padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer",
              background: on ? "var(--color-primary-16)" : "transparent",
              color: on ? "var(--color-primary-500)" : "var(--color-text-3)",
              whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Grid "table" with search + client-side pagination, matching the reference.
 * columns: [{ key, label, width='1fr', align, render(row) }]
 * rows: array of objects
 * searchKeys: array of row keys to match the search box against
 */
export function DataTable({
  columns, rows, perPage = 8, searchKeys, searchPlaceholder = "Search...",
  emptyText = "No rows.", title, right, defaultSortKey = null, defaultSortDir = "asc",
  onRowClick = null,
}) {
  const tableId = `data-table-${useId().replace(/:/g, "")}`;
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  // Initial sort is just seeded state — the user can still click any header to
  // re-sort (which flips to that column, asc), so nothing is locked in.
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultSortDir);

  const filtered = useMemo(() => {
    if (!q.trim() || !searchKeys?.length) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(needle)));
  }, [rows, q, searchKeys]);

  // Client-side sort. A column sorts on `sortValue(row)` if given, else row[key].
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return filtered;
    const val = (r) => (col.sortValue ? col.sortValue(r) : r[sortKey]);
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;      // nulls last
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "en-GB", { numeric: true }) * dir;
    });
  }, [filtered, sortKey, sortDir, columns]);

  const onSort = (c) => {
    if (c.sortable === false) return;
    if (sortKey === c.key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(c.key); setSortDir("asc"); }
    setPage(1);
  };

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const p = Math.min(Math.max(1, page), totalPages);
  const start = (p - 1) * perPage;
  const pageRows = sorted.slice(start, start + perPage);
  const template = columns.map((c) => c.width || "1fr").join(" ");

  return (
    <Card>
      {(title || searchKeys) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          {title ? <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700 }}>{title}</div> : <span />}
          <div style={{ display: "flex", flexWrap: "wrap", minWidth: 0, gap: 12, alignItems: "center", flex: "1 1 200px", justifyContent: "flex-end" }}>
            {right}
            {searchKeys && (
              <input
                data-testid={`${tableId}-search`}
                aria-label={searchPlaceholder}
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder={searchPlaceholder}
                style={{
                  flex: 1, minWidth: 0, width: "100%", maxWidth: 340, background: "var(--color-bg-panel)",
                  border: "1px solid rgba(255,255,255,0.12)", borderRadius: 9,
                  padding: "10px 14px", color: "var(--color-text-1)", fontSize: 14,
                  fontFamily: "var(--font-family)",
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* header row (click to sort, unless column has sortable:false) */}
      <div data-testid={`${tableId}-scroll`} className="dc-table-scroll" role="region" aria-label={typeof title === "string" ? title : "Data table"} tabIndex={0} style={{ maxWidth: "100%", overflowX: "auto" }}>
      <div style={{ minWidth: Math.max(320, columns.length * 120) }}>
      <div style={{ display: "grid", gridTemplateColumns: template, columnGap: 12, padding: "0 4px 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-4)", borderBottom: "1px solid var(--hairline)" }}>
        {columns.map((c) => {
          const canSort = c.sortable !== false && c.label;
          const active = sortKey === c.key;
          return (
            <div
              key={c.key}
              aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
              style={{ textAlign: c.align || "left", cursor: canSort ? "pointer" : "default", userSelect: "none", color: active ? "var(--color-text-2)" : undefined }}
            >
              {canSort ? <button type="button" data-testid={`${tableId}-sort-${c.key}`} onClick={() => onSort(c)} style={{ border: 0, background: "none", color: "inherit", font: "inherit", cursor: "pointer", textAlign: "inherit" }}>{c.label} {active ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}</button> : c.label}
              {c.hint && <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", verticalAlign: "middle" }}><InfoTip text={c.hint} /></span>}
            </div>
          );
        })}
      </div>

      {/* data rows */}
      {pageRows.map((r, i) => (
        <div key={i} data-testid={`${tableId}-row-${start + i}`} role={onRowClick ? "button" : undefined} tabIndex={onRowClick ? 0 : undefined} onKeyDown={onRowClick ? e => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onRowClick(r); } } : undefined} onClick={onRowClick ? () => onRowClick(r) : undefined} style={{ display: "grid", gridTemplateColumns: template, alignItems: "center", columnGap: 12, padding: "13px 4px", borderBottom: "1px solid var(--hairline-soft)", fontSize: 13.5, cursor: onRowClick ? "pointer" : undefined }}>
          {columns.map((c) => (
            <div
              key={c.key}
              style={{
                textAlign: c.align || "left",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                fontVariantNumeric: c.numeric ? "tabular-nums" : undefined,
                color: c.color,
              }}
              title={c.truncate ? String(r[c.key] ?? "") : undefined}
            >
              {c.render ? c.render(r) : r[c.key]}
            </div>
          ))}
        </div>
      ))}

      {total === 0 && (
        <div style={{ padding: "28px 4px", textAlign: "center", color: "var(--color-text-3)", fontSize: 14 }}>{emptyText}</div>
      )}
      </div></div>

      {/* pager */}
      {total > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
          <div style={{ fontSize: 13, color: "var(--color-text-3)" }}>
            Showing {start + 1}–{Math.min(start + perPage, total)} of {total.toLocaleString("en-GB")}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" data-testid={`${tableId}-previous`} disabled={p <= 1}
              onClick={() => setPage(Math.max(1, p - 1))}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", fontSize: 13, fontWeight: 600, cursor: "pointer", color: p <= 1 ? "#4a4166" : "var(--color-text-2)" }}
            >Prev</button>
            <div style={{ padding: "7px 12px", fontSize: 13, color: "var(--color-text-3)" }}>Page {p} of {totalPages}</div>
            <button type="button" data-testid={`${tableId}-next`} disabled={p >= totalPages}
              onClick={() => setPage(Math.min(totalPages, p + 1))}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", fontSize: 13, fontWeight: 600, cursor: "pointer", color: p >= totalPages ? "#4a4166" : "var(--color-text-2)" }}
            >Next</button>
          </div>
        </div>
      )}
    </Card>
  );
}
